import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import moment from 'moment';
import * as types from '../../constants/prayerFeatureActionTypes';
import * as consts from '../../constants/prayerFeatureConstants';
import YesNoModal from "../common/YesNoComponent";

function CommentCard({
    memberId, commentId, postId, onEdit, getComment, dispatchCommentSuccess, dispatchCommentFailure,
    onActionComplete, addCommentReaction, removeCommentReaction, deleteComment
}) {
    const [comment, setComment] = useState(null);
    const [commentDropdownOpen, setCommentDropdownOpen] = useState(false);
    const [selectedComment, setSelectedComment] = useState('');
    const [deletingComment, setDeletingComment] = useState(null);

    useEffect(() => {
        let _liveObject = null;
        if (comment === null) {
            getComment(commentId, (liveObject) => {
                _liveObject = liveObject;
                if (liveObject) {
                    if (liveObject.model) {
                        dispatchCommentSuccess(liveObject.model);
                        setComment(liveObject.model);
                    }
                    liveObject.on('dataUpdated', model => {
                        dispatchCommentSuccess(model);
                        setComment(model);
                    });
                    liveObject.on('dataError', error => {
                        dispatchCommentFailure(error);
                        console.log('comment (dataError)', comment, error);
                    });
                }
            });
        }

        return () => {
            if (_liveObject) {
                _liveObject.dispose();
            }
        };
    }, []);

    const commentToggle = () => {
        setCommentDropdownOpen(!commentDropdownOpen);
    };

    const _deleteComment = (comment) => {
        deleteComment(comment, success => {
            onActionComplete(success ?
                { success, confirmationMessage: 'Your comment has been successfully deleted.' } :
                { success, confirmationMessage: 'Oh no! We encountered an issue while deleting your comment. Please try again later.' },
                !success
            );
        });
        setDeletingComment(null);
    };

    const reactionCallback = success => {
        if (!success) {
            onActionComplete({ success, confirmationMessage: consts.ACTION_ERROR}, true);
        }
    };

    const handleCommentReaction = (comment, postId) => {
        if (!comment.myReactions.includes("love")) {
            addCommentReaction(comment.commentId, postId, reactionCallback);
        } else {
            removeCommentReaction(comment.commentId, postId, reactionCallback);
        }
    };

    const timeDifference = (current, previous) => {
        const elapsed = current - previous;
        const hours = Math.round(elapsed / (60 * 60 * 1000));
        return `${hours <= 1 ? 'Just Now' : hours + ' Hours Ago'}`;
    };

    const isCreatedHoursAgo = (dates, hours) => {
        const fourtyEightHr = 1000 * 60 * 60 * hours;
        const fourtyEightHrAgo = Date.now() - fourtyEightHr;
        return dates > fourtyEightHrAgo;
    };

    const setCommentTime = (timeCreatedAt) => {
        const createdDateInMiliSec = new Date(timeCreatedAt).getTime();
        const createdTime = moment(timeCreatedAt).format('LL');
        const relativeCreatedTime = moment(timeCreatedAt).fromNow();
        const commentTime = timeAgo(isCreatedHoursAgo, createdDateInMiliSec, relativeCreatedTime, createdTime, Date.now())
        return commentTime
    }

    const timeAgo = (isCreatedHoursAgo, createdDateInMiliSec, relativeCreatedTime, createdTime, currentTime) => {
        if (isCreatedHoursAgo(createdDateInMiliSec, 24)) {
            return timeDifference(currentTime, createdDateInMiliSec);
        } else if (isCreatedHoursAgo(createdDateInMiliSec, 48)) {
            return 'Yesterday';
        } else {
            return createdTime;
        }
    };

    if (comment == null || comment.isDeleted) return null;

    return (
        <div className={"prayer-card comment-form-container comment-prayer-card" + (memberId === comment.userId ? " author" : (comment.user.roles.length !== 0 && comment.user.roles[0] === 'moderator') ? " prayer-card-chaplain" : "")}>
            <div className="prayer-card-header d-flex">
                <div>
                    <span>{comment.user.roles.length !== 0 && comment.user.roles[0] === 'moderator' ? "Replied to Author" : "Author Updated"} &#8231; </span>
                    {setCommentTime(comment.editedAt)}{comment.createdAt.toString() !== comment.editedAt.toString() && <span> &#8231; Edited</span>}
                </div>
                <div className="d-flex ml-auto">
                    {memberId === comment.userId &&
                        <>
                            <Dropdown className="prayer-options" toggle={commentToggle} direction="left"
                                isOpen={commentDropdownOpen && selectedComment === comment.commentId}
                                onClick={() => { setSelectedComment(comment.commentId); }} >
                                <DropdownToggle>
                                    <span className="icon-share-outline" />
                                </DropdownToggle>
                                <DropdownMenu>
                                    <DropdownItem onClick={() => onEdit({ commentId: comment.commentId, text: comment.data.text, isComment: true })} tag="button">
                                        Edit
                                        </DropdownItem>
                                    <DropdownItem onClick={() => setDeletingComment(comment)} tag="button">
                                        Remove
                                        </DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                            {deletingComment &&
                                <YesNoModal showModal={deletingComment !== null} modalText={consts.DELETE_COMMENT_CONFIRM}
                                    modalNoFunction={() => setDeletingComment(null)} modalYesFunction={_deleteComment} YesParameter={deletingComment}
                                />
                            }
                        </>
                    }
                </div>
            </div>
            <div className="prayer-card-body">
                {comment.user.roles.length !== 0 && comment.user.roles[0] === 'moderator' && <div className="prayer-card-title">Chaplain</div>}
                <div className="prayer-card-text">{comment.data.text}</div>
            </div>
            <div className="prayer-card-footer d-flex justify-content-between">
                <div className="d-flex prayer-card-reactions"
                    onClick={() => handleCommentReaction(comment, postId)}
                >
                    <div className={comment.myReactions.includes('love') ? 'svg-reaction-red-heart' : 'svg-reaction-white-heart'} />
                    <div className="reactions-count">{comment.reactionsCount}</div>
                </div>
            </div>
        </div>
    );
}

const mapDispatchToProps = dispatch => {
    return {
        getComment: (commentId, callback) => dispatch({ type: types.GET_COMMENT, commentId, callback }),
        dispatchCommentSuccess: comment => dispatch({ type: types.GET_COMMENT_SUCCESS, comment }),
        dispatchCommentFailure: error => dispatch({ type: types.GET_COMMENT_FAILURE, error }),
        addCommentReaction: (commentId, postId, callback) => {
            dispatch({ type: types.ADD_COMMENT_REACTION, commentId, postId, callback });
        },
        removeCommentReaction: (commentId, postId, callback) => {
            dispatch({ type: types.REMOVE_COMMENT_REACTION, commentId, postId, callback });
        },
        deleteComment: (comment, callback) => {
            dispatch({ type: types.DELETE_COMMENT, comment, callback });
        }
    };
};

export default connect(null, mapDispatchToProps)(CommentCard);